import { isValidEmail, isValidIco, isValidPsc, phoneProblem } from '../validation/rules';
import { compareDecimal, deepEqual, fractionDigits, isDecimal, isEmpty, isoDate, isUuid } from './values';
import { isRichText, richTextIsBlank, richTextLength, type RichTextValue } from './richText';
import { cells, filledCells, isTableValue, type TableValue } from './table';
import {
  canBeRequired,
  isLeaf,
  type Condition,
  type EvaluationContext,
  type EvaluationResult,
  type FieldDef,
  type FieldError,
  type FieldState,
  type FieldType,
  type FormDefinition,
  type FormValues,
  type JsonValue,
  type LeafCondition,
} from './types';

type Scope = Map<string, JsonValue | null>;

/**
 * Evaluates show/hide, enable/disable and conditional-required rules and validates active fields.
 * Semantics identical to the backend FormEvaluator (shared fixtures in spec/form-engine/cases):
 * visible = parent visible ∧ visibleWhen (a section or repeating group without fields is hidden); an option with a
 * rule is offered only while it holds, and a chosen option that is not offered is dropped like a hidden value;
 * enabled = visible ∧ parent enabled ∧ enabledWhen;
 * required = enabled ∧ (required ∨ requiredWhen). Hidden/disabled fields count as empty for later conditions,
 * are not validated and are not part of `values` (the client still keeps what the user typed).
 * validWhen (D26): an active field with an otherwise valid value must meet it (rule.invalid); it is not checked while
 * a field it compares with (valueField) is empty.
 */
export function evaluate(definition: FormDefinition, values: FormValues | null | undefined, context: EvaluationContext = {}): EvaluationResult {
  const run = new Run(definition, context);
  const input = values && typeof values === 'object' && !Array.isArray(values) ? values : {};
  const out: FormValues = {};
  for (const field of definition.fields) run.field(field, true, true, input, null, '', out);
  return { state: run.state, values: out, errors: run.errors, complete: run.errors.length === 0 };
}

class Run {
  readonly index = new Map<string, FieldDef>();
  readonly state: Record<string, FieldState> = {};
  readonly errors: FieldError[] = [];
  readonly scope: Scope = new Map();

  constructor(
    definition: FormDefinition,
    private readonly ctx: EvaluationContext,
  ) {
    this.indexFields(definition.fields);
  }

  private indexFields(fields: FieldDef[]) {
    for (const f of fields) {
      if (!this.index.has(f.key)) this.index.set(f.key, f);
      this.indexFields(f.children ?? []);
    }
  }

  field(f: FieldDef, parentVisible: boolean, parentEnabled: boolean, source: Record<string, JsonValue>, row: Scope | null, prefix: string, out: FormValues) {
    // a group without fields (just added in the builder) counts as hidden
    const emptyGroup = (f.type === 'SECTION' || f.type === 'REPEATING_GROUP') && !f.children?.length;
    const visible = parentVisible && !emptyGroup && this.test(f.visibleWhen, row);
    const enabled = visible && parentEnabled && this.test(f.enabledWhen, row);
    const required = enabled && canBeRequired(f.type) && (Boolean(f.required) || (f.requiredWhen !== undefined && this.test(f.requiredWhen, row)));
    const path = prefix + f.key;
    // options with a rule are offered only while it holds
    const offered = visible && (f.options ?? []).some((o) => o.visibleWhen) ? (f.options ?? []).filter((o) => this.test(o.visibleWhen, row)).map((o) => o.value) : undefined;
    this.state[path] = offered ? { visible, enabled, required, options: offered } : { visible, enabled, required };
    switch (f.type) {
      case 'INFO':
        return;
      case 'SECTION':
        for (const child of f.children ?? []) this.field(child, visible, enabled, source, row, prefix, out);
        return;
      case 'REPEATING_GROUP':
        this.group(f, visible, enabled, required, source[f.key], path, out, row);
        return;
      default:
        this.simple(f, visible && enabled, required, source[f.key], path, out, row, offered);
    }
  }

  private setRef(f: FieldDef, row: Scope | null, value: JsonValue | null) {
    (row ?? this.scope).set(f.key, value);
  }

  private simple(f: FieldDef, active: boolean, required: boolean, input: JsonValue | undefined, path: string, out: FormValues, row: Scope | null, offered?: string[]) {
    if (!active) return this.setRef(f, row, null);
    // a switch is never empty while active: no value means off
    let value = f.type === 'SWITCH' && isEmpty(input) ? false : input;
    // formatted text without any text is empty
    if (f.type === 'RICH_TEXT' && isRichText(value) && richTextIsBlank(value)) value = null;
    // a table keeps its filled cells only (none: empty)
    if (f.type === 'TABLE' && isTableValue(f, value)) value = filledCells(f, value) as JsonValue | null;
    // a chosen option that is not offered is dropped, like the value of a hidden field
    if (offered && !isEmpty(value) && typeOk(f.type, value!)) value = withoutHiddenOptions(f, value!, offered);
    if (isEmpty(value)) {
      if (required) this.errors.push({ field: path, code: 'required' });
      return this.setRef(f, row, null);
    }
    if (f.type === 'TABLE' ? !isTableValue(f, value) : !typeOk(f.type, value!)) {
      this.errors.push({ field: path, code: 'invalidType' });
      return this.setRef(f, row, null);
    }
    const error = this.constraintError(f, value!, path, required);
    if (error) this.errors.push(error);
    out[f.key] = value!;
    this.setRef(f, row, value!);
    // the validation rule sees the field's own value (set above) and the values of earlier fields
    if (!error && f.validWhen && this.comparable(f.validWhen, row) && !this.test(f.validWhen, row)) this.errors.push({ field: path, code: 'rule.invalid' });
  }

  /** The value a reference ("key" or "row.key") stands for: null when the field is empty, hidden or disabled. */
  private lookup(ref: string, row: Scope | null): JsonValue | null {
    return ref.startsWith('row.') ? (row?.get(ref.slice(4)) ?? null) : (this.scope.get(ref) ?? null);
  }

  /** Every field the rule compares with (valueField) has a value: until then the rule cannot be checked. */
  private comparable(c: Condition, row: Scope | null): boolean {
    if ('all' in c) return c.all.every((x) => this.comparable(x, row));
    if ('any' in c) return c.any.every((x) => this.comparable(x, row));
    if ('not' in c) return this.comparable(c.not, row);
    return c.valueField === undefined || !isEmpty(this.lookup(c.valueField, row));
  }

  private group(f: FieldDef, visible: boolean, enabled: boolean, required: boolean, value: JsonValue | undefined, path: string, out: FormValues, parentRow: Scope | null) {
    const active = visible && enabled;
    if (!active) {
      if (Array.isArray(value)) {
        value.forEach((r, i) => {
          const rowObj = isObject(r) ? r : {};
          const rowScope: Scope = new Map();
          for (const child of f.children ?? []) this.field(child, visible, enabled, rowObj, rowScope, `${path}[${i}].`, {});
        });
      }
      return this.setRef(f, parentRow, null);
    }
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      if (required) this.errors.push({ field: path, code: 'required' });
      return this.setRef(f, parentRow, null);
    }
    if (!Array.isArray(value)) {
      this.errors.push({ field: path, code: 'invalidType' });
      return this.setRef(f, parentRow, null);
    }
    const c = f.constraints ?? {};
    if (c.minRows !== undefined && value.length < c.minRows) this.errors.push({ field: path, code: 'rows.min', params: { min: c.minRows } });
    else if (c.maxRows !== undefined && value.length > c.maxRows) this.errors.push({ field: path, code: 'rows.max', params: { max: c.maxRows } });
    const outRows: FormValues[] = [];
    value.forEach((r, i) => {
      const rowObj = isObject(r) ? r : {};
      const outRow: FormValues = {};
      const rowScope: Scope = new Map();
      for (const child of f.children ?? []) this.field(child, true, true, rowObj, rowScope, `${path}[${i}].`, outRow);
      outRows.push(outRow);
    });
    out[f.key] = outRows;
    this.setRef(f, parentRow, value);
  }

  private constraintError(f: FieldDef, value: JsonValue, path: string, required: boolean): FieldError | null {
    const c = f.constraints ?? {};
    switch (f.type) {
      case 'TEXT': {
        const s = value as string;
        const max = c.maxLength ?? 255;
        if (s.length > max) return { field: path, code: 'maxLength', params: { max } };
        const format = c.format ?? 'none';
        if (format === 'phone') {
          // what is wrong with the number: format.phone, format.phonePrefix, … (validation.phone… → format.phone…)
          const problem = phoneProblem(s);
          return problem ? { field: path, code: `format.${problem.key.slice('validation.'.length)}`, ...(problem.params ? { params: problem.params } : {}) } : null;
        }
        const ok = format === 'email' ? isValidEmail(s.trim()) : format === 'ico' ? isValidIco(s) : format === 'psc' ? isValidPsc(s) : true;
        return ok ? null : { field: path, code: `format.${format}` };
      }
      case 'TEXTAREA': {
        const max = c.maxLength ?? 8000;
        return (value as string).length > max ? { field: path, code: 'maxLength', params: { max } } : null;
      }
      case 'RICH_TEXT': {
        const max = c.maxLength ?? 8000;
        return richTextLength(value as unknown as RichTextValue) > max ? { field: path, code: 'maxLength', params: { max } } : null;
      }
      case 'TABLE': {
        // the first cell (row by row) that is too long, else – when required – the first empty cell
        const table = value as unknown as TableValue;
        const max = c.maxLength ?? 255;
        const long = cells(f).find((x) => (table[x.row]?.[x.column] ?? '').length > max);
        if (long) return { field: path, code: 'maxLength', params: { max, row: long.row, column: long.column } };
        const empty = required ? cells(f).find((x) => table[x.row]?.[x.column] === undefined) : undefined;
        return empty ? { field: path, code: 'table.incomplete', params: { row: empty.row, column: empty.column } } : null;
      }
      case 'NUMBER': {
        const s = value as string;
        if (!isDecimal(s)) return { field: path, code: 'number.format' };
        if (c.decimals !== undefined && fractionDigits(s) > c.decimals) return { field: path, code: 'number.decimals', params: { decimals: c.decimals } };
        if (c.min !== undefined && isDecimal(c.min) && compareDecimal(s, c.min) < 0) return { field: path, code: 'number.min', params: { min: c.min } };
        if (c.max !== undefined && isDecimal(c.max) && compareDecimal(s, c.max) > 0) return { field: path, code: 'number.max', params: { max: c.max } };
        return null;
      }
      case 'DATE': {
        const d = isoDate(value);
        if (!d) return { field: path, code: 'date.format' };
        const min = isoDate(c.min);
        const max = isoDate(c.max);
        if (min && d < min) return { field: path, code: 'date.min', params: { min } };
        if (max && d > max) return { field: path, code: 'date.max', params: { max } };
        const tf = this.ctx.timeframe;
        if (c.relative === 'WITHIN_TIMEFRAME' && tf && (d < tf.from || d > tf.to)) return { field: path, code: 'date.timeframe', params: { from: tf.from, to: tf.to } };
        if (c.relative === 'ON_OR_BEFORE_START' && tf?.from && d > tf.from) return { field: path, code: 'date.onOrBeforeStart', params: { from: tf.from } };
        if (c.relative === 'ON_OR_AFTER_TODAY' && this.ctx.today && d < this.ctx.today) return { field: path, code: 'date.onOrAfterToday', params: { today: this.ctx.today } };
        return null;
      }
      case 'SINGLE_CHOICE':
        return (f.options ?? []).some((o) => o.value === value) ? null : { field: path, code: 'choice.invalid' };
      case 'MULTI_CHOICE': {
        const arr = value as string[];
        const seen = new Set<string>();
        for (const v of arr) {
          if (!(f.options ?? []).some((o) => o.value === v) || seen.has(v)) return { field: path, code: 'choice.invalid' };
          seen.add(v);
        }
        if (c.minSelected !== undefined && arr.length < c.minSelected) return { field: path, code: 'choice.minSelected', params: { min: c.minSelected } };
        if (c.maxSelected !== undefined && arr.length > c.maxSelected) return { field: path, code: 'choice.maxSelected', params: { max: c.maxSelected } };
        return null;
      }
      case 'TEAM_MEMBER': {
        const team = this.ctx.teamMembers;
        return team && !team.some((m) => m.id === value) ? { field: path, code: 'choice.invalid' } : null;
      }
      case 'AUTOCOMPLETE':
        // the id of a record of the source register; whether it exists is checked by the server
        return isUuid(value) ? null : { field: path, code: 'reference.invalid' };
      default:
        return null;
    }
  }

  test(c: Condition | undefined, row: Scope | null): boolean {
    if (!c) return true;
    if ('all' in c) return c.all.every((x) => this.test(x, row));
    if ('any' in c) return c.any.some((x) => this.test(x, row));
    if ('not' in c) return !this.test(c.not, row);
    return this.leaf(c, row);
  }

  private leaf(leaf: LeafCondition, row: Scope | null): boolean {
    const key = leaf.field.startsWith('row.') ? leaf.field.slice(4) : leaf.field;
    const v = this.lookup(leaf.field, row);
    const type: FieldType = this.index.get(key)?.type ?? 'TEXT';
    // a fixed value, or the value of another field (empty: no comparison holds, like with an empty field)
    const expected = leaf.valueField !== undefined ? this.lookup(leaf.valueField, row) : leaf.value;
    switch (leaf.op) {
      case 'empty':
        return isEmpty(v);
      case 'notEmpty':
        return !isEmpty(v);
      case 'eq':
        return equal(type, v, expected);
      case 'neq':
        return !equal(type, v, expected);
      case 'in':
        return inList(v, expected);
      case 'notIn':
        return !inList(v, expected);
      case 'includes':
        return includes(v, expected);
      case 'excludes':
        return !includes(v, expected);
      case 'gt': {
        const r = compare(type, v, expected);
        return r !== null && r > 0;
      }
      case 'gte': {
        const r = compare(type, v, expected);
        return r !== null && r >= 0;
      }
      case 'lt': {
        const r = compare(type, v, expected);
        return r !== null && r < 0;
      }
      case 'lte': {
        const r = compare(type, v, expected);
        return r !== null && r <= 0;
      }
      default:
        return false;
    }
  }
}

function isObject(v: JsonValue | undefined): v is Record<string, JsonValue> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function typeOk(type: FieldType, v: JsonValue): boolean {
  switch (type) {
    case 'TEXT':
    case 'TEXTAREA':
    case 'NUMBER':
    case 'DATE':
    case 'SINGLE_CHOICE':
    case 'TEAM_MEMBER':
    case 'AUTOCOMPLETE':
      return typeof v === 'string';
    case 'MULTI_CHOICE':
      return Array.isArray(v) && v.every((x) => typeof x === 'string');
    case 'YES_NO':
    case 'SWITCH':
      return typeof v === 'boolean';
    case 'RICH_TEXT':
      return isRichText(v);
    default:
      return true;
  }
}

/** The choice without the options that are not offered now (unknown values stay: they are reported as invalid). */
function withoutHiddenOptions(f: FieldDef, value: JsonValue, offered: string[]): JsonValue | null {
  const hidden = (x: JsonValue) => typeof x === 'string' && !offered.includes(x) && (f.options ?? []).some((o) => o.value === x);
  if (f.type === 'SINGLE_CHOICE') return hidden(value) ? null : value;
  if (f.type === 'MULTI_CHOICE' && Array.isArray(value)) return value.filter((x) => !hidden(x));
  return value;
}

function equal(type: FieldType, v: JsonValue | null, expected: JsonValue | undefined): boolean {
  if (isEmpty(v) || expected === undefined) return false;
  if (type === 'MULTI_CHOICE') {
    if (!Array.isArray(v) || !Array.isArray(expected)) return false;
    const a = new Set(v.map(String));
    const b = new Set(expected.map(String));
    return a.size === b.size && [...a].every((x) => b.has(x));
  }
  if (type === 'NUMBER' && isDecimal(v) && isDecimal(expected)) return compareDecimal(v, expected) === 0;
  return deepEqual(v, expected);
}

function inList(v: JsonValue | null, expected: JsonValue | undefined): boolean {
  if (isEmpty(v) || typeof v !== 'string' || !Array.isArray(expected)) return false;
  return expected.some((x) => x === v);
}

function includes(v: JsonValue | null, expected: JsonValue | undefined): boolean {
  if (!Array.isArray(v) || typeof expected !== 'string') return false;
  return v.some((x) => x === expected);
}

/** null when not comparable (empty, invalid, wrong type). */
function compare(type: FieldType, v: JsonValue | null, expected: JsonValue | undefined): number | null {
  if (isEmpty(v) || typeof v !== 'string' || typeof expected !== 'string') return null;
  if (type === 'NUMBER') return isDecimal(v) && isDecimal(expected) ? compareDecimal(v, expected) : null;
  if (type === 'DATE') {
    const a = isoDate(v);
    const b = isoDate(expected);
    return a && b ? (a < b ? -1 : a > b ? 1 : 0) : null;
  }
  return null;
}

export { isLeaf };
